import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback } from 'react';
import {
  PiAnchorSimpleThin,
  PiBoatThin,
  PiHouseLineThin,
  PiListChecksThin,
  PiPersonThin,
} from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { TargetTab } from '../targetTypes';
import { useTargetContext } from '../TargetContext';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';

const noop = () => {};

interface TabButtonProps {
  icon: React.ElementType;
  value: string;
  disabled: boolean;
  onClick: (value: any) => void;
  tooltipText: string;
}

interface TabsComponentProps {
  setTab: React.Dispatch<React.SetStateAction<TargetTab>>;
  tab: TargetTab;
  imo: string | undefined;
}

const TabButton = ({
  icon: Icon,
  value,
  disabled,
  onClick,
  tooltipText,
}: TabButtonProps) => {
  return (
    <Tooltip>
      <TabsTrigger asChild value={value}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'justify-center size-8',
              disabled && 'opacity-50 cursor-default',
            )}
            onClick={disabled ? noop : () => onClick(value)}
          >
            <Icon
              className={cn(
                'size-4 flex-shrink-0',
                !disabled && 'group-hover/btn:text-primary',
              )}
            ></Icon>
          </Button>
        </TooltipTrigger>
      </TabsTrigger>
      <TooltipContent className="ml-4">{tooltipText}</TooltipContent>
    </Tooltip>
  );
};

export const TabsDetails = ({ tab, setTab, imo }: TabsComponentProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();
  const { viewMode } = useViewMode();

  const { setIsExpanded } = useTargetContext();

  const onClick = useCallback(
    (value: TargetTab) => {
      setTab(value);
      if (setIsExpanded) {
        setIsExpanded(true);
      }
    },
    [setTab, setIsExpanded],
  );

  // Check if we're in detail view mode
  const isDetailView = viewMode === ViewMode.Detail;

  return (
    <Tabs value={tab} className="">
      <TabsList>
        <TabButton
          icon={PiHouseLineThin}
          value="base"
          disabled={false}
          onClick={onClick}
          tooltipText={messages.shipDetailsTabBaseTooltip}
        ></TabButton>
        <TabButton
          icon={PiBoatThin}
          value="advancedVessel"
          disabled={!permissions.mdmAdvancedVesselInfo}
          onClick={onClick}
          tooltipText={
            permissions.mdmAdvancedVesselInfo
              ? messages.shipDetailsTabAdvancedVesselInformationTooltip
              : messages.shipDetailsTabAdvancedVesselInformationTooltipDisable
          }
        ></TabButton>
        <TabButton
          icon={PiPersonThin}
          value="register"
          disabled={!permissions.mdmRegister}
          onClick={onClick}
          tooltipText={
            permissions.mdmRegister
              ? messages.shipDetailsTabRegisterTooltip
              : messages.shipDetailsTabRegisterTooltipDisable
          }
        ></TabButton>
        <TabButton
          icon={PiListChecksThin}
          value="integrity"
          disabled={!permissions.mdmIntegrity}
          onClick={onClick}
          tooltipText={
            permissions.mdmIntegrity
              ? messages.shipDetailsTabIntegrityTooltip
              : messages.shipDetailsTabIntegrityTooltipDisable
          }
        ></TabButton>
        <TabButton
          icon={PiAnchorSimpleThin}
          value="nextPort"
          disabled={!permissions.nextport || !imo || isDetailView}
          onClick={onClick}
          tooltipText={
            permissions.nextport
              ? imo
                ? isDetailView
                  ? 'Next Port is not available in detail view'
                  : messages.shipDetailsTabNextPortTooltip
                : messages.shipDetailsTabNextPortTooltipNoIMO
              : messages.shipDetailsTabNextPortTooltipDisable
          }
        ></TabButton>
      </TabsList>
    </Tabs>
  );
};
